import * as servicesApi from '../commons/testimServicesApi';
import { getLogger } from '../commons/logger';
import { promiseMap } from '../utils';
import { gridMessages } from '../commons/constants';
import { GridError, GridConcurrencyError } from '../errors';

const logger = getLogger('slot-service');

export interface SerializedSlot {
    slotId: string;
    executionId: string;
    testResultId: string;
    lastKeepAlive: number;
    source?: string;
    type: 'execution' | 'authoring';
    sessionUrl: string;
}

const slotsCache: Record<number, Partial<SerializedSlot>> = {};
let keepAliveTimer: NodeJS.Timeout | null = null;

export const addItemToSlotsCache = (workerId: number, slot: Partial<SerializedSlot>) => {
    slotsCache[workerId] = slot;
};

export async function releaseCompanySlot(workerId: number, companyId: string, projectId: string) {
    const slotData = slotsCache[workerId];
    if (!slotData) {
        return;
    }

    const { slotId } = slotData;
    delete slotsCache[workerId];
    if (!slotId) {
        logger.warn('failed to find slot slot id', { companyId, workerId });
        return;
    }

    logger.info('release slot id', { companyId, slotId });
    try {
        await servicesApi.releaseCompanySlot(companyId, projectId, slotId);
    } catch (err) {
        logger.error('failed to release slot', { companyId, err });
    }
}

async function _keepAlive(companyId: string, projectId: string) {
    const slots = Object.values(slotsCache).filter(Boolean);

    if (slots.length === 0) {
        return;
    }

    try {
        await servicesApi.keepAliveCompanySlots(companyId, projectId, slots);
    } catch (err) {
        logger.error('failed to update slot keep alive', { err, slots, companyId });
    }
}

function startKeepAlive(companyId: string, projectId: string) {
    const KEEP_ALIVE_INTERVAL = 10 * 1000;
    keepAliveTimer = global.setInterval(() => _keepAlive(companyId, projectId), KEEP_ALIVE_INTERVAL);
}

async function releaseAllSlots(companyId: string, projectId: string) {
    const workerIds = Object.keys(slotsCache);

    if (!workerIds.length) {
        return;
    }

    logger.warn('not all slots released before end runner flow', { companyId });
    try {
        await promiseMap(workerIds, workerId => releaseCompanySlot(Number(workerId), companyId, projectId));
    } catch (err) {
        logger.error('failed to release all slots', { err, companyId });
    }
}

async function endKeepAlive(companyId: string, projectId: string) {
    await releaseAllSlots(companyId, projectId);
    clearInterval(keepAliveTimer!);
}

export const keepAlive = {
    start: startKeepAlive,
    end: endKeepAlive,
};

export async function requestSlot(companyId: string, projectId: string, workerId: number) {
    let res;
    try {
        res = await servicesApi.requestCompanySlot(companyId, projectId);
    } catch (err) {
        logger.error('failed to get slot', { companyId, err });
        throw new Error(gridMessages.UNKNOWN);
    }
    logger.info('get slot info', { ...res, companyId });
    if (!res || (!['success', 'error'].includes(res.status))) {
        logger.error('invalid response - get slot', { res });
        throw new Error(gridMessages.UNKNOWN);
    }

    if (res.status === 'success') {
        addItemToSlotsCache(workerId, { slotId: res.slotId });
        return res.slotId;
    }

    if (res.code === 'not-found') {
        throw new GridError(gridMessages.NOT_FOUND);
    }

    if (res.code === 'no-available-slot') {
        throw new GridConcurrencyError('Failed to run test - concurrency limit reached');
    }

    logger.error('invalid code error response - get slot', { res });
    throw new GridError(gridMessages.UNKNOWN);
}

export async function updateSlotDataCache(companyId: string, projectId: string, slotData: Partial<SerializedSlot>, workerId: number) {
    const slot = slotsCache[workerId];
    if (!slot) {
        throw new Error('slot not found');
    }
    Object.assign(slot, slotData);
    addItemToSlotsCache(workerId, slot);
    await servicesApi.updateCompanySlot(companyId, projectId, slot);
    logger.info('update slot data on server', { companyId, projectId, slotData });
}
